import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MessageSquare, CheckCircle } from 'lucide-react';

interface AppointmentData {
  subject: string;
  message: string;
  date: string;
  time: string;
  appointmentType: string;
}

interface AppointmentFormProps {
  appointmentData: AppointmentData;
  selectedDate: string;
  selectedTime: string;
  isSubmitting: boolean;
  submitError: string | null;
  onInputChange: (field: keyof AppointmentData, value: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  onClearError: () => void;
}

const subjects = [
  'Développement d\'application web',
  'Création d\'application mobile',
  'Consultation en IA',
  'Refonte de site existant',
  'E-commerce / Boutique en ligne',
  'Système de gestion (ERP/CRM)',
  'Audit technique',
  'Formation équipe',
  'Maintenance et support',
  'Autre (préciser dans le message)'
];

const AppointmentForm: React.FC<AppointmentFormProps> = ({
  appointmentData,
  selectedDate,
  selectedTime,
  isSubmitting,
  submitError,
  onInputChange,
  onSubmit,
  onBack,
  onClearError
}) => {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <motion.div
      key="step2"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="bg-white rounded-2xl p-8 shadow-lg max-w-4xl mx-auto"
    >
      <h2 className="text-3xl font-bold text-gray-900 mb-8">Détails du rendez-vous</h2>
      
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Sujet du rendez-vous *
        </label>
        <select
          value={appointmentData.subject}
          onChange={(e) => onInputChange('subject', e.target.value)}
          className="input-field w-full"
          required
        >
          <option value="">Sélectionnez le sujet de votre rendez-vous</option>
          {subjects.map((subject) => (
            <option key={subject} value={subject}>{subject}</option>
          ))}
        </select>
      </div>
      
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          💬 Message pour l'administrateur
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Partagez des détails sur votre projet, vos besoins spécifiques ou toute information qui pourrait aider l'équipe à mieux préparer votre rendez-vous.
        </p>
        <textarea
          value={appointmentData.message}
          onChange={(e) => onInputChange('message', e.target.value)}
          className="input-field w-full h-32 resize-none border-2 border-blue-200 focus:border-blue-500"
          placeholder="Exemple: Je souhaite développer une application mobile pour mon restaurant. J'aimerais discuter des fonctionnalités, du budget et des délais..."
        />
        <div className="mt-2 text-xs text-gray-400">
          Ce message sera visible par l'équipe IRIS SOFTECH pour mieux vous accompagner.
        </div>
      </div>
      
      {/* Récapitulatif */}
      <div className="bg-gray-50 rounded-xl p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">📅 Récapitulatif de votre rendez-vous</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="flex items-center space-x-3">
            <Calendar className="w-5 h-5 text-blue-600" />
            <span className="text-gray-700">{formatDate(selectedDate)}</span>
          </div>
          <div className="flex items-center space-x-3">
            <Clock className="w-5 h-5 text-blue-600" />
            <span className="text-gray-700">{selectedTime}</span>
          </div>
          <div className="flex items-center space-x-3">
            {appointmentData.appointmentType === 'online' ? (
              <>
                <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 text-xs">💻</span>
                </div>
                <span className="text-gray-700">En ligne</span>
              </>
            ) : (
              <>
                <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 text-xs">🏢</span>
                </div>
                <span className="text-gray-700">Physique</span>
              </>
            )}
          </div>
        </div>
        
        {/* Afficher le message s'il y en a un */}
        {appointmentData.message && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
            <div className="flex items-start space-x-2">
              <MessageSquare className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">Votre message :</p>
                <p className="text-sm text-blue-800">{appointmentData.message}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Affichage des erreurs */}
      {submitError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <div className="w-5 h-5 text-red-500">⚠️</div>
            </div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-red-800 mb-1">Erreur</h4>
              <p className="text-sm text-red-700 whitespace-pre-line">{submitError}</p>
            </div>
            <button
              onClick={onClearError}
              className="flex-shrink-0 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="btn-secondary"
        >
          Précédent
        </button>
        <button
          onClick={onSubmit}
          disabled={!appointmentData.subject || isSubmitting}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Confirmation en cours...</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>Confirmer le rendez-vous</span>
            </>
          )}
        </button>
      </div>
    </motion.div>
  );
};

export default AppointmentForm;
